@import "tailwindcss";
@import "uniwind";

/* Served from `public/icons/` (`configs/expo/public:sync-icons`). Expo's CSS pipeline leaves a
   relative url() unresolved, so it 404'd against each page's path. */
@font-face {
  font-family: "haniverse-icons";
  src: url("/icons/haniverse-icons.woff2") format("woff2");
  font-display: block;
}

@plugin 'tailwindcss-animate';

@source './src';
@source '../../packages/dev/improve/haniverse';

@custom-variant dark (&:where(.dark, .dark *));

@theme inline {
  --breakpoint-xs: 30rem;

  /* The ONE font the app ships: every source face (Noto Sans, Noto Sans SC/TC, Hanazono,
     BabelStone, …) is subsetted and merged into it by `assets/fonts/han-composite`. Unquoted on
     purpose — uniwind hands the value to native `fontFamily` verbatim. */
  --font-han-composite: Han Composite;
  --font-sans: Han Composite;
  /* Web-only public website (landing, support, legal pages): its copy is all Latin, and iOS
     Safari renders the variable Han Composite face at its thin default instance, so it uses the
     platform UI font (SF Pro on Apple, Roboto on Android) with Han Composite as the fallback for
     any CJK glyph. */
  --font-website:
    system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Han Composite", sans-serif;

  --font-weight-normal: 400;
  --font-weight-medium: 500;
  --font-weight-semibold: 600;
  --font-weight-bold: 700;
  --font-weight-extrabold: 800;

  --color-border: hsl(var(--border));
  --color-input: hsl(var(--input));
  --color-ring: hsl(var(--ring));
  --color-background: hsl(var(--background));
  --color-foreground: hsl(var(--foreground));
  --color-primary: hsl(var(--primary));
  --color-primary-foreground: hsl(var(--primary-foreground));
  --color-secondary: hsl(var(--secondary));
  --color-secondary-foreground: hsl(var(--secondary-foreground));
  --color-destructive: hsl(var(--destructive));
  --color-destructive-foreground: hsl(var(--destructive-foreground));
  --color-muted: hsl(var(--muted));
  --color-muted-foreground: hsl(var(--muted-foreground));
  --color-accent: hsl(var(--accent));
  --color-accent-foreground: hsl(var(--accent-foreground));
  --color-popover: hsl(var(--popover));
  --color-popover-foreground: hsl(var(--popover-foreground));
  --color-surface: hsl(var(--surface));
  --color-surface-header: hsl(var(--surface-header));
  --color-surface-foreground: hsl(var(--surface-foreground));

  /* Fixed white / black / orange palette of the web-only public website (landing, support, legal
     and account-deletion pages), independent of the app's light/dark theme. The pages are built
     from `@-/dev.improve.haniverse.components.website`, so a change here restyles all of them. */
  --color-website-accent: #ff7a3d;
  --color-website-accent-glow: #ffd9c4;
  --color-website-canvas: #f9f7f3;
  --color-website-ink: #0a0a0a;
  /* Hardware colors of the landing's phone mockups (an iPhone and a Pixel-style Android phone). */
  --color-landing-iphone-highlight: #4a4a4f;
  --color-landing-iphone-body: #1d1d20;
  --color-landing-iphone-button: #2b2b2f;
  --color-landing-android-highlight: #5b6068;
  --color-landing-android-body: #26292d;
  --color-landing-android-button: #33363b;
  --color-landing-android-camera-ring: #111214;
  /* The landing's pinyin examples: the app's light-mode tone 1 and tone 4 colors, fixed because
     the website ignores the visitor's color scheme. */
  --color-landing-tone-1: #e53935;
  --color-landing-tone-4: #8e24aa;

  /* Pinyin tone colors — drive `text-tone-*` utilities so slide copy can color a
     word by its tone. The raw per-mode values live in the `:root` light/dark
     blocks below (so the color flips with the color scheme); these tokens just
     expose them to the Tailwind `text-tone-*` utilities. Mirrors
     DEFAULT_TONE_COLOR_SETTINGS in lib/morpheme/grapheme/+entities/default-tone-color-settings.ts
     (tone-1…tone-4 = tone1…tone4; tone-5 = neutral tone0). */
  --color-tone-1: var(--tone-1);
  --color-tone-2: var(--tone-2);
  --color-tone-3: var(--tone-3);
  --color-tone-4: var(--tone-4);
  --color-tone-5: var(--tone-5);

  /* Mastery-stage colors — bronze, silver, gold, and neon orange. Drives
     `text-stage-*` / `bg-stage-*` / `border-stage-*` utilities for the stats
     screen. Raw per-mode hex values live in the `:root` light/dark blocks below
     so each color can remain legible in both color schemes. */
  --color-stage-new: var(--stage-new);
  --color-stage-familiar: var(--stage-familiar);
  --color-stage-proficient: var(--stage-proficient);
  --color-stage-mastered: var(--stage-mastered);

  /* Study-activity heatmap ramp (the GitHub-style contribution grid on the stats
     screen) — an orange scale from the empty-day surface through four intensity
     steps. Raw per-mode values live in the `:root` light/dark blocks below. */
  --color-activity-0: var(--activity-0);
  --color-activity-1: var(--activity-1);
  --color-activity-2: var(--activity-2);
  --color-activity-3: var(--activity-3);
  --color-activity-4: var(--activity-4);

  /* App surfaces use XS; circles and pills use Tailwind's built-in rounded-full. */
  --radius-*: initial;
  --radius-xs: 0.125rem;

  --animate-accordion-down: accordion-down 0.2s ease-out;
  --animate-accordion-up: accordion-up 0.2s ease-out;

  @keyframes accordion-down {
    from {
      height: 0;
    }
    to {
      height: var(--radix-accordion-content-height);
    }
  }

  @keyframes accordion-up {
    from {
      height: var(--radix-accordion-content-height);
    }
    to {
      height: 0;
    }
  }
}

@utility border-hairline {
  border-width: calc(hairlineWidth());
}

@layer theme {
  :root {
    @variant light {
      --background: 40 33.3% 96.5%; /* shared warm beige off-white, #f9f7f3 */
      --foreground: 0 0% 3.9%;
      --surface: 0 0% 100%;
      --surface-header: 240 25% 98.43%; /* apple.com's cool off-white nav fill, #fafafc */
      --surface-foreground: 0 0% 3.9%;
      --popover: 0 0% 100%;
      --popover-foreground: 0 0% 3.9%;
      --primary: 0 0% 9%;
      --primary-foreground: 0 0% 98%;
      --secondary: 0 0% 100%;
      --secondary-foreground: 0 0% 0%;
      --muted: 40 33.3% 96.5%; /* warm beige off-white, #f9f7f3 */
      --muted-foreground: 0 0% 45.1%;
      --accent: 0 0% 90%; /* neutral gray #e6e6e6 for press, hover and selected states */
      --accent-foreground: 0 0% 9%;
      --destructive: 0 84.2% 60.2%;
      --destructive-foreground: 0 0% 98%;
      --border: 0 0% 89.8%;
      --input: 0 0% 89.8%;
      --ring: 0 0% 63%;
      --chart-1: 12 76% 61%;
      --chart-2: 173 58% 39%;
      --chart-3: 197 37% 24%;
      --chart-4: 43 74% 66%;
      --chart-5: 27 87% 67%;

      /* Light-mode pinyin tone colors — DEFAULT_TONE_COLOR_SETTINGS *LightHex
         (tone-5 = neutral tone0). */
      --tone-1: #e53935;
      --tone-2: #43a047;
      --tone-3: #1e88e5;
      --tone-4: #8e24aa;
      --tone-5: #9e9e9e;

      /* Light-mode mastery-stage colors. */
      --stage-new: #ce8946; /* bronze */
      --stage-familiar: #c4c4c4; /* silver */
      --stage-proficient: #efbf04; /* gold */
      --stage-mastered: #ff7a3d; /* neon orange */

      /* Light-mode review-streak ramp — empty, under-threshold yellow, then three orange
         steps from the streak floor (lightest) to the lifetime peak (darkest). */
      --activity-0: #ededed;
      --activity-1: #ffe9a3;
      --activity-2: #ffd9c2;
      --activity-3: #ffab78;
      --activity-4: #d94f12;
    }

    @variant dark {
      --background: 0 0% 3.9%;
      --foreground: 0 0% 98%;
      --surface: 0 0% 3.9%;
      --surface-header: 0 0% 14.9%;
      --surface-foreground: 0 0% 98%;
      --popover: 0 0% 3.9%;
      --popover-foreground: 0 0% 98%;
      --primary: 0 0% 98%;
      --primary-foreground: 0 0% 9%;
      --secondary: 0 0% 100%;
      --secondary-foreground: 0 0% 0%;
      --muted: 0 0% 14.9%;
      --muted-foreground: 0 0% 63.9%;
      --accent: 0 0% 25%;
      --accent-foreground: 0 0% 98%;
      --destructive: 0 70.9% 59.4%;
      --destructive-foreground: 0 0% 98%;
      --border: 0 0% 14.9%;
      --input: 0 0% 14.9%;
      --ring: 300 0% 45%;
      --chart-1: 220 70% 50%;
      --chart-2: 160 60% 45%;
      --chart-3: 30 80% 55%;
      --chart-4: 280 65% 60%;
      --chart-5: 340 75% 55%;

      /* Dark-mode pinyin tone colors — DEFAULT_TONE_COLOR_SETTINGS *DarkHex
         (tone-5 = neutral tone0). Lighter/brighter so they read on a dark bg. */
      --tone-1: #ef9a9a;
      --tone-2: #a5d6a7;
      --tone-3: #90caf9;
      --tone-4: #ce93d8;
      --tone-5: #bdbdbd;

      /* Dark-mode mastery-stage colors — lighter/brighter so they read on a dark bg. */
      --stage-new: #d7a86e; /* bronze */
      --stage-familiar: #d6d6d6; /* silver */
      --stage-proficient: #ffd54f; /* gold */
      --stage-mastered: #ff9466; /* neon orange */

      /* Dark-mode review-streak ramp — empty is a lifted surface; yellow then orange
         steps so under-threshold and streak days stay distinct on a dark card. */
      --activity-0: #262626;
      --activity-1: #6b5a1a;
      --activity-2: #5c2a10;
      --activity-3: #99461a;
      --activity-4: #ff9466;
    }
  }
}
